// Global safety net for every API request the web app makes.
//
// Installed once (main.tsx) around window.fetch, so it covers the ~55 feature
// services that call fetch() directly as well as utils/api.ts and axios (XHR
// is not covered; axios has its own interceptor). It never changes what a
// request asks for; it only makes the answer more robust:
//   - the Authorization header is always the FRESHEST token in localStorage
//     (many screens still read the token from the Zustand store, which was
//     stale after a silent refresh — the "only F5 fixes it" bug);
//   - a 401 triggers one single-flight refresh and a replay of the request;
//   - a network blip (the browser's raw "Failed to fetch", or a 502/503/504
//     while the API restarts during a deploy) is retried for reads, and a
//     "connexion interrompue" event lets ConnectionBanner tell the user instead
//     of leaving a raw error on the screen.
// Writes are never retried: an action may already have reached the server.
import { isPublicPath } from '@/utils/publicPaths';

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
const ACCESS_KEY = 'nexedu_access_token';
const REFRESH_KEY = 'nexedu_refresh_token';

export const CONNECTION_LOST_EVENT = 'nexedu:connection-lost';
export const CONNECTION_RESTORED_EVENT = 'nexedu:connection-restored';
export const SESSION_EXPIRED_EVENT = 'nexedu:session-expired';
export const TOKEN_REFRESHED_EVENT = 'nexedu:token-refreshed';

const READ_METHODS = new Set(['GET', 'HEAD']);
const RETRY_DELAYS_MS = [600, 1200, 2400];
const GATEWAY_STATUSES = new Set([502, 503, 504]);
// A 429 on a read is waited out once (the API says when its window resets),
// instead of surfacing "Too Many Requests" on a screen that only needed a moment.
const RATE_LIMIT_STATUS = 429;
const MAX_RATE_LIMIT_WAIT_MS = 20_000;

let nativeFetch: typeof window.fetch | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let connectionLost = false;

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function isApiUrl(url: string): boolean {
  return url.startsWith(API_BASE_URL);
}

function isAuthUrl(url: string): boolean {
  return url.includes('/api/auth/');
}

/** The request as sent, with the Authorization header refreshed when the caller set one. */
function withFreshToken(init: RequestInit | undefined): RequestInit {
  const headers = new Headers(init?.headers ?? undefined);
  const token = readStorage(ACCESS_KEY);
  if (token && headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, headers };
}

function emit(name: string): void {
  window.dispatchEvent(new CustomEvent(name));
}

function noteFailure(): void {
  if (!connectionLost) {
    connectionLost = true;
    emit(CONNECTION_LOST_EVENT);
  }
}

function noteSuccess(): void {
  if (connectionLost) {
    connectionLost = false;
    emit(CONNECTION_RESTORED_EVENT);
  }
}

function clearSessionAndRedirect(): void {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem('nexedu_user');
    localStorage.removeItem('nexedu-auth-storage');
  } catch {
    // storage unavailable: nothing to clear
  }
  emit(SESSION_EXPIRED_EVENT);
  if (!isPublicPath(window.location.pathname)) {
    window.location.href = '/login';
  }
}

/** One refresh at a time; concurrent 401s all wait for the same answer. */
function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const fetchFn = nativeFetch ?? window.fetch;
  refreshInFlight = (async () => {
    const refreshToken = readStorage(REFRESH_KEY);
    if (!refreshToken) return false;
    try {
      const res = await fetchFn(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken?: string; refreshToken?: string };
      if (!data.accessToken) return false;
      localStorage.setItem(ACCESS_KEY, data.accessToken);
      if (data.refreshToken) localStorage.setItem(REFRESH_KEY, data.refreshToken);
      emit(TOKEN_REFRESHED_EVENT);
      return true;
    } catch {
      return false;
    } finally {
      // Let the awaiting callers read the result before the next refresh can start.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** How long the API asked us to wait (Retry-After or X-RateLimit-Reset, seconds), or null when too long to be worth it. */
function rateLimitWaitMs(response: Response): number | null {
  const header = response.headers.get('retry-after') ?? response.headers.get('x-ratelimit-reset');
  const seconds = header ? Number(header) : NaN;
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const ms = Math.ceil(seconds) * 1000 + 250;
  return ms <= MAX_RATE_LIMIT_WAIT_MS ? ms : null;
}

async function resilientFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const fetchFn = nativeFetch as typeof window.fetch;
  const url = urlOf(input);

  // Anything that is not our API (CDN, R2, external) is left untouched.
  if (!isApiUrl(url) || input instanceof Request) {
    return fetchFn(input, init);
  }

  const method = (init?.method ?? 'GET').toUpperCase();
  const isRead = READ_METHODS.has(method);
  const hadAuth = new Headers(init?.headers ?? undefined).has('Authorization');

  const send = (): Promise<Response> => fetchFn(url, withFreshToken(init));

  let response: Response | null = null;
  let waitedForRateLimit = false;
  for (let attempt = 0; ; attempt++) {
    try {
      response = await send();
    } catch (err) {
      const networkError = err instanceof TypeError;
      if (networkError && isRead && attempt < RETRY_DELAYS_MS.length) {
        noteFailure();
        await delay(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      if (networkError) noteFailure();
      throw err;
    }
    if (GATEWAY_STATUSES.has(response.status) && isRead && attempt < RETRY_DELAYS_MS.length) {
      noteFailure();
      await delay(RETRY_DELAYS_MS[attempt]);
      continue;
    }
    if (response.status === RATE_LIMIT_STATUS && isRead && !waitedForRateLimit) {
      const wait = rateLimitWaitMs(response);
      if (wait !== null) {
        waitedForRateLimit = true;
        await delay(wait);
        continue;
      }
    }
    break;
  }

  noteSuccess();

  if (response.status === 401 && hadAuth && !isAuthUrl(url)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Replay once with the new token. A body that is a stream cannot be
      // replayed; strings, FormData and Blobs can.
      return send();
    }
    clearSessionAndRedirect();
  }

  return response;
}

/** Install once, before the app renders. Safe to call twice. */
export function installResilientFetch(): void {
  if (nativeFetch) return;
  nativeFetch = window.fetch.bind(window);
  window.fetch = resilientFetch as typeof window.fetch;
}
